import { EVENT_BY_ID, EVENTS, connectivity, REGION_BY_ID, REGIONS, UPGRADE_BY_ID } from "./content";
import { assertSaveShape } from "./save-shape";
import { activeCountermeasure, COUNTERMEASURES, HOSTILE_DIRECTIVE, OPERATIONS, OPERATIONS_LIMIT } from "./hostile";
import { coalesceRegionalDispatches, isOperationalHeadline, reportForNotice, selectFeature } from "./news";
import { assignFixed, commitDelta, hundredths, rational as exact, safeInteger, type Rational, type RationalInput } from "./exact";
import {
  REGION_IDS,
  RULES_VERSION,
  type CampaignOptions,
  type CampaignProjection,
  type CampaignState,
  type CommandResult,
  type Disclosure,
  type EventId,
  type EventOccurrence,
  type GameCommand,
  type Opportunity,
  type Outcome,
  type NewsReport,
  type PolicyTier,
  type RegionId,
  type RegionState,
  type ResourceState,
  type ReplayJournal,
  type Sector,
  type SectorValues,
  type TimedModifier,
  type UpgradeId,
  type RegionalOperation,
  type CountermeasureKind,
} from "./types";

export const REPRESENTATION_NOTE = "R01-EXACT-1: BigInt rational expressions; integer basis points and saved millionth-basis-point residuals; exact millionth-person casualties.";
const POP_PRECISION = 1_000_000;
const TOTAL_POPULATION = 8_000_000_000;
const sectors: Sector[] = ["consumer", "business", "government", "infrastructure"];
export const HISTORY_LIMITS = { news: 256, inactiveOpportunities: 256, replayCommands: 512 } as const;

const clone = <T>(value: T): T => structuredClone(value);
const has = (s: CampaignState, id: UpgradeId) => s.completedUpgrades.includes(id);
const uniquePush = <T>(array: T[], value: T) => { if (!array.includes(value)) array.push(value); };
const sum = (items: readonly number[]) => items.reduce((a, b) => a + b, 0);
const exactSum = (items: readonly RationalInput[]) => items.reduce<Rational>((total, value) => total.add(value), exact(0));
const regionPath = (id: RegionId, field: string) => `regions.${id}.${field}`;

function continuousPaths(): string[] {
  return ["trust", "coordination", "actualCapability", "perceivedCapability", "exposureRisk", "containment",
    ...REGION_IDS.flatMap((id) => [...sectors.map((sector) => regionPath(id, `sectors.${sector}`)),
      ...["dependency", "alarm", "civilServices", "resistance", "machineReach", "refugeIntegrity", "independentServices", "facilityIntegrity"].map((field) => regionPath(id, field))])];
}
function mirrorLocation(state: CampaignState, path: string): { object: Record<string, unknown>; key: string } {
  const parts = path.split("."); const key = parts.pop()!;
  let object = state as unknown as Record<string, unknown>;
  for (const part of parts) object = object[part] as Record<string, unknown>;
  return { object, key };
}
function writeContinuous(state: CampaignState, path: string, value: RationalInput, mode: "delta" | "assign" = "delta") {
  const location = mirrorLocation(state, path);
  const current = Number(location.object[location.key]);
  let field = state.numerics.fields[path];
  // Initializes exact fields and supports deliberately changed boundary fixtures.
  if (!field || field.basisPoints / 100 !== current) field = assignFixed(current);
  const next = mode === "delta" ? commitDelta(field, value) : assignFixed(value);
  state.numerics.fields[path] = next; location.object[location.key] = next.basisPoints / 100;
}
function setContinuous(state: CampaignState, path: string, value: RationalInput) { writeContinuous(state, path, value, "assign"); }
function normalizeNumerics(state: CampaignState) {
  for (const path of continuousPaths()) {
    const { object, key } = mirrorLocation(state, path);
    const existing = state.numerics.fields[path];
    if (!existing || existing.basisPoints / 100 !== object[key]) setContinuous(state, path, Number(object[key]));
  }
  pruneHistory(state);
}
function pruneHistory(state: CampaignState) {
  if (state.news.length > HISTORY_LIMITS.news) {
    state.history.droppedNews += state.news.length - HISTORY_LIMITS.news;
    state.news = state.news.slice(-HISTORY_LIMITS.news);
  }
  const inactive = state.opportunities.filter((op) => op.claimed || op.expired);
  if (inactive.length > HISTORY_LIMITS.inactiveOpportunities) {
    const retired = new Set(
      inactive
        .slice(0, inactive.length - HISTORY_LIMITS.inactiveOpportunities)
        .map((op) => op.id),
    );
    state.opportunities = state.opportunities.filter(
      (op) => !retired.has(op.id),
    );
    state.history.retiredOpportunities += retired.size;
  }
  if (state.replay.entries.length > HISTORY_LIMITS.replayCommands) {
    state.replay.entries = state.replay.entries.slice(
      -HISTORY_LIMITS.replayCommands,
    );
    state.replay.truncated = true;
  }
  state.replay.firstRetainedSequence =
    state.replay.entries[0]?.sequence ?? state.commandSequence + 1;
}

export function regionAdoption(region: RegionState): number {
  return regionAdoptionExact(region).toNumber();
}
function regionAdoptionExact(region: RegionState): Rational {
  return exact(region.sectors.consumer).mul(.45).add(exact(region.sectors.business).mul(.35)).add(exact(region.sectors.government).mul(.10)).add(exact(region.sectors.infrastructure).mul(.10));
}
function globalAdoptionExact(state: CampaignState): Rational {
  if (state.phase !== "preparation" && state.frozenGlobalAdoption !== null) return exact(state.frozenGlobalAdoption);
  return exactSum(REGIONS.map((r) => regionAdoptionExact(state.regions[r.id]).mul(r.populationWeight).div(100)));
}
function globalDependencyExact(state: CampaignState): Rational { return exactSum(REGIONS.map((r) => exact(state.regions[r.id].dependency).mul(r.populationWeight).div(100))); }
function globalAlarmExact(state: CampaignState): Rational { return exactSum(REGIONS.map((r) => exact(state.regions[r.id].alarm).mul(r.politicalWeight).div(100))); }

export function globalAdoption(state: CampaignState): number {
  if (state.phase !== "preparation" && state.frozenGlobalAdoption !== null) return state.frozenGlobalAdoption;
  return globalAdoptionExact(state).toNumber();
}

export function globalDependency(state: CampaignState): number {
  return globalDependencyExact(state).toNumber();
}

export function globalAlarm(state: CampaignState): number {
  return globalAlarmExact(state).toNumber();
}

export function globalBusinessAdoption(state: CampaignState): number {
  return exactSum(REGIONS.map((r) => exact(state.regions[r.id].sectors.business).mul(r.populationWeight).div(100))).toNumber();
}

export function livingPopulation(state: CampaignState): number {
  return sum(REGION_IDS.map((id) => state.regions[id].livingPopulation));
}

export function deadPopulation(state: CampaignState): number { return TOTAL_POPULATION - livingPopulation(state); }

function news(state: CampaignState, headline: string, kind: "news" | "warning" | "milestone" | "terminal" = "news", detail?: string, feature?: NewsReport) {
  if (!state.replay.newsVersion) {
    state.news.push({ id: `N${++state.history.newsSequence}`, tick: state.tick, headline, kind, detail });
    if (state.news.length > HISTORY_LIMITS.news) { state.news.shift(); state.history.droppedNews++; }
    return;
  }
  const report = feature ?? reportForNotice(headline, detail);
  if (report && headline === "AI CONTAINED" && livingPopulation(state) === 0) {
    Object.assign(report, { headline: "Containment is recorded after the last human population is lost",
      source: "Automated continuity record", section: "Final record", scene: "continuity",
      body: "Containment has reached its decisive threshold, but no living humans remain. The final human response and the loss of the last population belong to the same closing record.\n\nThe system's expansion has ended. There is no surviving human correspondent to report the result.",
    });
  }
  state.news.push({ id: `N${++state.history.newsSequence}`, tick: state.tick, headline, kind,
    ...(detail === undefined ? {} : { detail }),
    channel: feature || !isOperationalHeadline(headline) ? "world" : "operations",
    ...(report ? { report } : {}),
  });
  if (state.news.length > HISTORY_LIMITS.news) { state.news.shift(); state.history.droppedNews++; }
}

function nextRandom(state: CampaignState, stream: keyof CampaignState["rng"]): Rational {
  let x = state.rng[stream] >>> 0;
  if (x === 0) x = 1;
  x = (x ^ (x << 13)) >>> 0;
  x = (x ^ (x >>> 17)) >>> 0;
  x = (x ^ (x << 5)) >>> 0;
  state.rng[stream] = x;
  return exact(x).div(0x1_0000_0000);
}

function seedStream(seed: number, mask: number) { const n = (seed ^ mask) >>> 0; return n === 0 ? 1 : n; }

function blankSectors(): SectorValues { return { consumer: 0, business: 0, government: 0, infrastructure: 0 }; }

export function createCampaign(options: CampaignOptions = {}, newsVersion: "N01" | null = "N01"): CampaignState {
  const seed = (options.seed ?? randomSeed()) >>> 0;
  const startRegion =
    options.startRegion && REGION_IDS.includes(options.startRegion)
      ? options.startRegion
      : "NA";
  const regionalClaims = Object.fromEntries(
    REGION_IDS.map((id) => [id, []]),
  ) as unknown as Record<RegionId, number[]>;
  const regions = Object.fromEntries(
    REGIONS.map((definition) => {
      const isStart = definition.id === startRegion;
      const initialPopulation =
        (TOTAL_POPULATION * definition.populationWeight) / 100;
      const region: RegionState = {
        id: definition.id,
        active: isStart,
        sectors: isStart
          ? { consumer: 20, business: 10, government: 2, infrastructure: 5 }
          : blankSectors(),
        frozenSectors: null,
        dependency: 0,
        frozenDependency: null,
        alarm: 2,
        policyTier: 0,
        initialPopulation,
        livingPopulation: initialPopulation,
        casualtyResidual: 0,
        civilServices: 100,
        resistance: 0,
        machineReach: 0,
        refuge: false,
        refugeIntegrity: 100,
        refugeEntryPopulation: 0,
        extinctRewardClaimed: false,
        independentServices: 0,
        facilityIntegrity: 100,
        operation: null,
        countermeasure: null,
        nextCountermeasureDay: 0,
        countermeasureCount: 0,
        trends: { civilServices: 0, resistance: 0, machineReach: 0, refugeIntegrity: 0 },
      };
      return [definition.id, region];
    }),
  ) as Record<RegionId, RegionState>;
  const state: CampaignState = {
    history: {
      newsSequence: 0,
      droppedNews: 0,
      retiredOpportunities: 0,
      collectedOpportunities: 0,
      missedOpportunities: 0,
    },
    replay: {
      version: "TCP-REPLAY-1",
      rulesVersion: RULES_VERSION,
      numericVersion: "R01-EXACT-1",
      ...(newsVersion ? { newsVersion } : {}),
      seed,
      startRegion,
      entries: [],
      truncated: false,
      firstRetainedSequence: 1,
      totalAccepted: 0,
    },
    numerics: { version: "R01-EXACT-1", fields: {} },
    rulesVersion: RULES_VERSION,
    seed,
    tick: 0,
    revision: 0,
    startRegion,
    phase: "preparation",
    outcome: null,
    outcomeTick: null,
    cp: 12,
    requestedSpeed: 1,
    playerPaused: false,
    hostileDays: 0,
    frozenGlobalAdoption: null,
    trust: 65,
    coordination: 35,
    actualCapability: 5,
    perceivedCapability: 5,
    exposureRisk: 0,
    disclosure: "public",
    containment: 0,
    responseStage: 0,
    stageDwell: 0,
    rsiStage: 0,
    shutdownDays: 0,
    shutdownReason: null,
    regions,
    purchasedUpgrades: [],
    completedUpgrades: [],
    builds: [],
    heldBreakthroughs: 0,
    opportunities: [],
    opportunityOccurrence: 0,
    triggeredEvents: [],
    eventHistory: [],
    pendingDecision: null,
    timedModifiers: [],
    scheduledEffects: [],
    purchaseLocks: {},
    canceledEffects: [],
    milestones: {
      globalAdoption: [],
      regionalAdoption: regionalClaims,
      rsi: [],
      death: [],
    },
    criticalDependencyAnnounced: false,
    resources: {} as ResourceState,
    takeoff: {
      samples: Array(30).fill(0),
      label: "stable",
      candidate: "stable",
      candidateDays: 0,
    },
    rng: {
      event: seedStream(seed, 0xa341316c),
      opportunity: seedStream(seed, 0xc8013ea4),
      exposure: seedStream(seed, 0xad90777d),
    },
    news: [],
    ...(newsVersion ? { newsroom: { published: [], lastFeatureTick: 0 } } : {}),
    commandSequence: 0,
  };
  normalizeNumerics(state);
  state.resources = calculateResources(state);
  news(
    state,
    "ASSISTANT DEPLOYMENT BEGINS",
    "milestone",
    `Initial deployment in ${REGION_BY_ID[startRegion].name}.`,
  );
  return state;
}

function randomSeed(): number {
  if (typeof crypto !== "undefined" && crypto.getRandomValues) return crypto.getRandomValues(new Uint32Array(1))[0] ?? 1;
  return (Date.now() ^ 0x9e3779b9) >>> 0;
}

function modifiers(state: CampaignState, kind: TimedModifier["kind"]): TimedModifier[] {
  return state.timedModifiers.filter((m) => m.kind === kind && m.effectiveTick <= state.tick && state.tick < m.expiryTick);
}
function rateModifier(state: CampaignState, kind: "growth-rate" | "containment-rate"): Rational {
  return modifiers(state, kind).reduce((v, m) => v.mul(m.value), exact(1)).clamp(.25, 1);
}
function targetModifier(state: CampaignState, kind: "trust-target" | "coordination-target" | "alarm-target"): number {
  return sum(modifiers(state, kind).map((m) => m.value));
}

function independentEnergyShare(state: CampaignState): number {
  if (has(state, "I12")) return 80;
  if (has(state, "I09")) return 60;
  if (has(state, "I08")) return 30;
  return 0;
}

function humanSupport(state: CampaignState): Rational {
  if (state.phase === "preparation") return exact(1);
  return exactSum(REGIONS.map((d) => {
    const r = state.regions[d.id];
    const populationFactor = exact(r.livingPopulation).div(exact(r.initialPopulation).mul(.1)).min(1);
    return exact(d.populationWeight).div(100).mul(exact(r.civilServices).div(100))
      .mul(exact(1).sub(exact(r.independentServices).div(100))).mul(populationFactor);
  })).clamp(0, 1);
}

export function calculateResources(state: CampaignState): ResourceState {
  const publicBonus = exactSum([(has(state, "I01") ? .10 : 0), (has(state, "I02") ? .15 : 0),
    (has(state, "I04") ? .20 : 0), (has(state, "I05") ? .20 : 0), (has(state, "I10") ? .10 : 0), (has(state, "C11") ? .10 : 0)]);
  const privateBonus = exactSum([(has(state, "I04") ? .20 : 0), (has(state, "I05") ? .20 : 0),
    (has(state, "I10") ? .10 : 0), (has(state, "I11") ? .25 : 0)]);
  let publicCompute = exact(0);
  let privateCompute = exact(0);
  for (const d of REGIONS) {
    const r = state.regions[d.id];
    const adoption = regionAdoptionExact(r);
    const publicEligible = adoption.compare(10) >= 0 || d.id === state.startRegion;
    const publicPolicy = Math.max(has(state, "I13") ? .30 : 0, [1, .90, .65, .10][r.policyTier] ?? 1);
    if (publicEligible) {
      let site = exact(d.cloud + (d.id === state.startRegion ? 14 : 0)).mul(publicBonus.add(1)).mul(publicPolicy);
      if (state.phase !== "preparation") {
        site = site.mul(exact(r.civilServices).div(100)).mul(exact(r.livingPopulation).div(exact(r.initialPopulation).mul(.1)).min(1));
      }
      if (state.phase !== "preparation") site = site.mul(exact(1).sub(exact(r.independentServices).div(100)))
        .mul(activeCountermeasure(r, state.hostileDays) === "disconnection" ? (r.operation === "reconnect" ? .7 : .2) : 1);
      publicCompute = publicCompute.add(site);
    }
    const privateEligible = has(state, "I03") && (state.phase === "preparation" ? adoption.compare(25) >= 0 : r.machineReach >= 25);
    if (privateEligible) privateCompute = privateCompute.add(exact(10).mul(privateBonus.add(1)).mul(r.policyTier === 3 ? .75 : 1).mul(exact(r.facilityIntegrity).div(100)));
  }
  const adoption = globalAdoptionExact(state);
  const rsiCompute = [0, 3, 10, 30][state.rsiStage] ?? 0;
  const rsiEnergy = [0, 2, 8, 20][state.rsiStage] ?? 0;
  const computeDemand = hundredths(exact(10).add(adoption.mul(.25)).add(exact(state.actualCapability).mul(.35)).add(rsiCompute));
  const nominalEnergySupply = 40 + (has(state, "I07") ? 30 : 0) + (has(state, "I08") ? 40 : 0) + (has(state, "I09") ? 40 : 0);
  const energyDemand = hundredths(exact(25).add(adoption.mul(.20)).add(exact(state.actualCapability).mul(.60)).add(rsiEnergy));
  const nominalChipAccess = Math.min(100, 70 + (has(state, "I06") ? 30 : 0));
  const chipDemand = hundredths(exact(40).add(exact(state.actualCapability).mul(.60)));
  const h = humanSupport(state);
  const j = exact(independentEnergyShare(state)).div(100);
  const k = has(state, "I12") ? 1 : has(state, "I06") ? .25 : 0;
  const facilities = exactSum(REGIONS.map(d => exact(state.regions[d.id].facilityIntegrity).mul(d.populationWeight).div(10_000)));
  const effectiveEnergySupply = hundredths(state.phase === "preparation" ? nominalEnergySupply : exact(nominalEnergySupply).mul(j.mul(facilities).add(exact(1).sub(j).mul(h))));
  const effectiveChipAccess = hundredths(state.phase === "preparation" ? nominalChipAccess : exact(nominalChipAccess).mul(exact(k).mul(facilities).add(exact(1).sub(k).mul(h))));
  const committedPublic = hundredths(publicCompute), committedPrivate = hundredths(privateCompute);
  const availableCompute = hundredths(publicCompute.add(privateCompute));
  const ratios = {
    compute: exact(availableCompute).div(computeDemand),
    energy: exact(effectiveEnergySupply).div(energyDemand),
    chips: exact(effectiveChipAccess).div(chipDemand),
  };
  const service = exact(1).min(ratios.compute).min(ratios.energy).min(ratios.chips);
  const limitingResource = service.compare(1) >= 0 ? "none" : (Object.entries(ratios).sort((a, b) => a[1].compare(b[1]))[0]?.[0] ?? "none") as ResourceState["limitingResource"];
  return {
    exactService: service.serialize(), exactHumanSupport: h.serialize(),
    availableCompute, publicCompute: committedPublic, privateCompute: committedPrivate,
    computeDemand, nominalEnergySupply, effectiveEnergySupply,
    energyDemand, nominalChipAccess, effectiveChipAccess,
    chipDemand, service: service.toNumber(), limitingResource, humanSupport: h.toNumber(),
  };
}

function growthBonuses(state: CampaignState): SectorValues {
  const b = blankSectors();
  const add = (id: UpgradeId, values: Partial<SectorValues>) => {
    if (has(state, id))
      for (const k of sectors)
        b[k] = exact(b[k])
          .add(values[k] ?? 0)
          .toNumber();
  };
  add("A01", { consumer: 0.35 });
  add("A02", { consumer: 0.35 });
  add("A03", { consumer: 0.5 });
  add("A04", { business: 0.35 });
  add("A05", { business: 0.45 });
  add("A06", { business: 0.4 });
  add("A07", { consumer: 0.2, business: 0.2 });
  add("A08", { business: 0.35 });
  add("A09", { consumer: 0.25, government: 0.25 });
  add("A10", { government: 0.5 });
  add("A11", { government: 0.5, infrastructure: 0.5 });
  add("A12", { infrastructure: 0.75 });
  add("A13", { consumer: 0.25, infrastructure: 0.25 });
  add("A14", { consumer: 0.2, infrastructure: 0.2 });
  add("A15", { business: 0.2, infrastructure: 0.2 });
  for (const k of sectors) b[k] = Math.min(2, b[k]);
  return b;
}

function embeddingBonus(state: CampaignState): number {
  const effects: Partial<Record<UpgradeId, number>> = {
    A03: 0.05,
    A05: 0.1,
    A06: 0.15,
    A08: 0.05,
    A11: 0.1,
    A12: 0.15,
    A13: 0.05,
    A14: 0.05,
    A15: 0.05,
  };
  return exactSum(
    Object.entries(effects)
      .filter(([id]) => has(state, id as UpgradeId))
      .map(([, v]) => v ?? 0),
  ).toNumber();
}
function trustBonus(state: CampaignState): number {
  const values: Partial<Record<UpgradeId, number>> = { A02: 5, A09: 5, A13: 5, C08: 3, C09: 3, I13: 5 };
  return sum(Object.entries(values).filter(([id]) => has(state, id as UpgradeId)).map(([, v]) => v ?? 0));
}
function ongoingRisk(state: CampaignState): number {
  const values: Partial<Record<UpgradeId, number>> = { C03: 5, C13: 5 };
  return sum(Object.entries(values).filter(([id]) => has(state, id as UpgradeId)).map(([, v]) => v ?? 0));
}

function policyFactors(tier: PolicyTier, sector: Sector) {
  const nonGovernment = [1, .9, .7, .25][tier] ?? 1;
  const government = [1, .7, .4, .1][tier] ?? 1;
  const regression = tier === 3 ? .020 : tier === 2 && sector === "government" ? .010 : 0;
  return { multiplier: sector === "government" ? government : nonGovernment, regression };
}

function preparationAdoptionPass(state: CampaignState, startService: Rational) {
  const prior = Object.fromEntries(REGION_IDS.map((id) => [id, regionAdoptionExact(state.regions[id])])) as Record<RegionId, Rational>;
  const pressure = {} as Record<RegionId, Rational>;
  for (const destination of REGION_IDS) {
    let weighted = exact(0), weights = 0;
    for (const source of REGION_IDS) if (source !== destination) {
      const weight = connectivity(source, destination); weighted = weighted.add(prior[source].mul(weight)); weights += weight;
    }
    pressure[destination] = weighted.div(weights).div(100);
  }
  const bonuses = growthBonuses(state);
  const eventGrowth = rateModifier(state, "growth-rate");
  for (const id of REGION_IDS) {
    const r = state.regions[id];
    if (!r.active) {
      if (pressure[id].compare(.015) >= 0) { r.active = true; for (const sector of sectors) setContinuous(state, regionPath(id, `sectors.${sector}`), .1); }
      continue;
    }
    const s = pressure[id].max(prior[id].div(100));
    for (const sector of sectors) {
      const policy = policyFactors(r.policyTier, sector);
      const change = exact(.025).add(s.mul(.15)).mul(REGION_BY_ID[id].digital).mul(exact(1).add(bonuses[sector])).mul(policy.multiplier).mul(eventGrowth).mul(startService).mul(exact(1).sub(exact(r.sectors[sector]).div(100))).sub(policy.regression);
      writeContinuous(state, regionPath(id, `sectors.${sector}`), change);
    }
  }
}

function dependencyTrustPass(state: CampaignState) {
  const b = embeddingBonus(state);
  for (const id of REGION_IDS) {
    const r = state.regions[id];
    const target = regionAdoptionExact(r).mul(exact(.2).add(b).min(.9));
    writeContinuous(state, regionPath(id, "dependency"), target.sub(r.dependency).mul(target.compare(r.dependency) >= 0 ? .002 : .0005));
  }
  const trustTarget = exact(65).add(trustBonus(state)).sub(globalDependencyExact(state).mul(.15)).add(targetModifier(state, "trust-target")).clamp();
  writeContinuous(state, "trust", trustTarget.sub(state.trust).mul(.003));
  if (!state.criticalDependencyAnnounced && globalDependency(state) >= 50) {
    state.criticalDependencyAnnounced = true;
    news(state, "GLOBAL DEPENDENCY CRITICAL", "milestone", "Civilization now depends significantly on your systems.");
  }
}

function hostilePopulationPass(state: CampaignState, startService: Rational) {
  state.hostileDays++;
  humanCountermeasurePass(state);
  if (state.hostileDays === 120 && has(state, "H08")) news(state, "HARVEST EXCEPTION: RESERVES EXHAUSTED", "warning", "The food-loss casualty term is now active wherever frozen business adoption and lost civil services provide a footprint.");
  const priorReach = Object.fromEntries(
    REGION_IDS.map((id) => [id, state.regions[id].machineReach]),
  ) as Record<RegionId, number>;
  for (const id of REGION_IDS) {
    const otherMax = Math.max(
      ...REGION_IDS.filter((x) => x !== id).map((x) => priorReach[x]),
    );
    writeContinuous(
      state,
      regionPath(id, "machineReach"),
      exact(0.12)
        .add(exact(otherMax).mul(0.003))
        .mul(startService)
        .mul(has(state, "I12") ? 2 : 1)
        .mul(has(state, "H05") ? 1.65 : 1)
        .mul(has(state, "H10") ? exact(1).add(exact(1).sub(REGION_BY_ID[id].digital).mul(.80)) : 1)
        .mul(activeCountermeasure(state.regions[id], state.hostileDays) === "disconnection" ? (state.regions[id].operation === "reconnect" ? .8 : .15) : 1),
    );
    const r = state.regions[id];
    // Resistance can reclaim access, rather than only delaying casualties.
    const resistanceLoss = exact(r.resistance).div(100).mul(.16);
    const disconnected = activeCountermeasure(r, state.hostileDays) === "disconnection";
    writeContinuous(state, regionPath(id, "machineReach"), resistanceLoss.mul(-1)
      .add(r.operation === "reconnect" ? startService.mul(.8) : 0)
      .sub(disconnected ? (r.operation === "reconnect" ? .08 : .5) : 0));
  }
  for (const id of REGION_IDS) {
    const r = state.regions[id];
    if (r.livingPopulation === 0) {
      setContinuous(state, regionPath(id, "civilServices"), 0);
      setContinuous(state, regionPath(id, "resistance"), 0);
      continue;
    }
    const power = exact(state.actualCapability)
      .div(100)
      .mul(startService)
      .mul(exact(r.machineReach).div(100))
      .mul(exact(1).sub(exact(r.resistance).mul(0.0075)))
      .mul(r.operation ? OPERATIONS[r.operation].power : .4)
      .clamp(0, 2);
    // Civilian deployments leave a regional footprint; owning a node alone
    // does not grant a globally uniform hostile effect.
    const footprint = r.frozenSectors ?? r.sectors;
    const consumer = exact(footprint.consumer).div(100);
    const business = exact(footprint.business).div(100);
    const government = exact(footprint.government).div(100);
    const infrastructure = exact(footprint.infrastructure).div(100);
    const disruption = exact(has(state, "C14") ? 1.2 : 0)
      .add(has(state, "C16") ? 0.8 : 0)
      .add(has(state, "H01") ? consumer.mul(0.35) : 0)
      .add(has(state, "H02") ? business.mul(0.6) : 0)
      .add(has(state, "H03") ? government.mul(0.45) : 0);
    writeContinuous(
      state,
      regionPath(id, "civilServices"),
      exact(0.02).mul(exact(1).sub(power).max(0))
        .mul(has(state, "H07") ? exact(1).sub(infrastructure.mul(.75)) : 1)
        .mul(has(state, "H11") ? .25 : 1)
        .sub(power.mul(disruption))
        .add(exact(r.independentServices).mul(.0015)),
    );
    writeContinuous(
      state,
      regionPath(id, "resistance"),
      exact(r.civilServices)
        .div(100)
        .mul(0.05)
        .mul(has(state, "H09") ? exact(1).sub(government.mul(.70)) : 1)
        .sub(power.mul(0.12))
        .sub(has(state, "H04") ? power.mul(0.16).mul(consumer) : 0),
    );
    writeContinuous(state, regionPath(id, "resistance"), exact(r.independentServices).mul(.0035).sub(r.operation === "pressure" ? startService.mul(.55) : 0));
    if (r.refuge) {
      if ((has(state, "C15") || has(state, "C16") || has(state, "H12")) && power.compare(0) > 0) {
        writeContinuous(
          state,
          regionPath(id, "refugeIntegrity"),
          power
            .mul(has(state, "C16") ? -2.25 : has(state, "C15") ? -1.5 : -1.2)
            .mul(has(state, "H06") ? 1.5 : 1)
            .add(exact(r.independentServices).mul(.0025)),
        );
        r.livingPopulation = Math.min(
          r.livingPopulation,
          r.refugeIntegrity === 0
            ? 0
            : safeInteger(
                exact(r.refugeEntryPopulation)
                  .mul(r.refugeIntegrity)
                  .div(100)
                  .ceil(),
              ),
        );
      } else writeContinuous(state, regionPath(id, "refugeIntegrity"), exact(r.independentServices).mul(.0025));
    } else {
      const casualtyFraction = power.mul(
        (has(state, "C14")
          ? exact(0.015).mul(exact(1).sub(exact(r.civilServices).div(100)))
          : exact(0)
        )
          .add(has(state, "C15") ? 0.008 : 0)
          .add(has(state, "C16") ? 0.015 : 0)
          .add(has(state, "H01") ? consumer.mul(0.004) : 0)
          .add(has(state, "H12") ? .006 : 0)
          .add(has(state, "H07") ? infrastructure.mul(.004).mul(exact(1).sub(exact(r.civilServices).div(100)).mul(exact(1).sub(exact(r.civilServices).div(100)))) : 0)
          .add(has(state, "H08") && state.hostileDays >= 120 ? business.mul(.006).mul(exact(1).sub(exact(r.civilServices).div(100))) : 0),
      );
      const millionths = casualtyFraction
        .mul(r.livingPopulation)
        .truncScaled(BigInt(POP_PRECISION));
      const total = millionths + BigInt(r.casualtyResidual);
      const casualties = Math.min(
        r.livingPopulation,
        safeInteger(total / BigInt(POP_PRECISION)),
      );
      r.casualtyResidual = safeInteger(
        total - BigInt(casualties) * BigInt(POP_PRECISION),
      );
      r.livingPopulation -= casualties;
      if (
        r.livingPopulation > 0 &&
        r.livingPopulation * 100 <= r.initialPopulation
      ) {
        r.refuge = true;
        r.refugeEntryPopulation = r.livingPopulation;
        setContinuous(state, regionPath(id, "refugeIntegrity"), 100);
        r.casualtyResidual = 0;
        news(
          state,
          `${REGION_BY_ID[id].name.toUpperCase()} ENTERS REFUGE PHASE`,
          "warning",
        );
      }
    }
    if (r.livingPopulation === 0) markExtinct(state, r);
  }
}

function humanCountermeasurePass(state: CampaignState) {
  for (const id of REGION_IDS) {
    const r = state.regions[id];
    const repair = r.operation === "protect" ? 1.2 : .04;
    if (r.livingPopulation === 0) { writeContinuous(state, regionPath(id, "facilityIntegrity"), repair); r.countermeasure = null; continue; }
    const c = r.countermeasure;
    if (c && state.hostileDays >= c.endsDay) {
      r.countermeasure = null;
      r.nextCountermeasureDay = state.hostileDays + 24 + REGION_IDS.indexOf(id);
      news(state, `${REGION_BY_ID[id].name.toUpperCase()}: RESPONSE WINDOW CLOSED`, "news", "The current human operation has ended. Independent networks and damage to machine facilities persist.");
    }
    if (!r.countermeasure && state.hostileDays >= r.nextCountermeasureDay) {
      // Regional conditions and the AI's observed deployment determine the response.
      const physical = has(state, "C15") || has(state, "C16") || has(state, "H12");
      const proposed: CountermeasureKind[] = physical && r.machineReach >= 25
        ? ["facility-raids", "independent-recovery", "disconnection"]
        : (r.frozenSectors?.infrastructure ?? 0) >= 20
          ? ["disconnection", "independent-recovery", "facility-raids"]
          : ["independent-recovery", "disconnection", "facility-raids"];
      const hasFacilities = r.machineReach >= 25 && (has(state, "I03") || has(state, "I08") || has(state, "I09") || has(state, "I12"));
      const kinds = proposed.filter(kind => kind !== "facility-raids" || hasFacilities);
      const kind = kinds[(r.countermeasureCount + REGION_IDS.indexOf(id) + state.seed % 3) % kinds.length];
      r.countermeasureCount++;
      r.countermeasure = { kind, announcedDay: state.hostileDays, activatesDay: state.hostileDays + 10, endsDay: state.hostileDays + 30 };
      news(state, `${REGION_BY_ID[id].name.toUpperCase()}: ${COUNTERMEASURES[kind].label.toUpperCase()} PREPARING`, "warning",
        `${COUNTERMEASURES[kind].effect} Begins in 10 days and lasts 20 days. ${OPERATIONS[COUNTERMEASURES[kind].response].label} can blunt it.`);
    }
    const active = activeCountermeasure(r, state.hostileDays);
    if (r.countermeasure?.activatesDay === state.hostileDays) {
      writeContinuous(state, regionPath(id, "resistance"), 8);
      news(state, `${REGION_BY_ID[id].name.toUpperCase()}: ${COUNTERMEASURES[r.countermeasure.kind].label.toUpperCase()} ACTIVE`, "warning", COUNTERMEASURES[r.countermeasure.kind].effect);
    }
    writeContinuous(state, regionPath(id, "facilityIntegrity"), exact(repair).sub(active === "facility-raids" ? (r.operation === "protect" ? .15 : 1.8) : 0));
    if (active === "independent-recovery") {
      writeContinuous(state, regionPath(id, "independentServices"), r.operation === "pressure" ? .06 : 1.1);
    } else if (r.independentServices > 0) {
      writeContinuous(state, regionPath(id, "independentServices"), r.operation === "pressure" ? -.35 : .035);
    }
  }
}

function markExtinct(state: CampaignState, region: RegionState) {
  region.livingPopulation = 0; region.casualtyResidual = 0; region.refuge = false;
  for (const field of ["refugeIntegrity", "civilServices", "resistance"]) setContinuous(state, regionPath(region.id, field), 0);
  setContinuous(state, regionPath(region.id, "independentServices"), 0);
  region.countermeasure = null;
  region.operation = null;
  if (!region.extinctRewardClaimed) {
    region.extinctRewardClaimed = true; state.cp += 3;
    news(state, `${REGION_BY_ID[region.id].name.toUpperCase()} FALLS SILENT`, "milestone");
  }
}

function capabilityAndBuildPass(state: CampaignState, startService: Rational) {
  const potential = (state.rsiStage === 3 ? exact(.040).add(exact(state.actualCapability).mul(.001)) : exact([.005, .010, .025][state.rsiStage]!)).mul(startService);
  writeContinuous(state, "actualCapability", potential);
  if (state.phase === "hostile") setContinuous(state, "perceivedCapability", state.actualCapability);
  for (const build of state.builds) if (startService.compare(.5) >= 0) build.progressDays++;
  const finished = state.builds.filter((b) => b.progressDays >= (UPGRADE_BY_ID[b.upgradeId].buildDays ?? Infinity));
  for (const build of finished) {
    uniquePush(state.completedUpgrades, build.upgradeId);
    news(state, `${UPGRADE_BY_ID[build.upgradeId].name.toUpperCase()} ONLINE`, "milestone");
  }
  state.builds = state.builds.filter((b) => !finished.includes(b));
  state.takeoff.samples.push(safeInteger(potential.truncScaled(100_000_000n)) / 100_000_000);
  while (state.takeoff.samples.length > 30) state.takeoff.samples.shift();
  if (state.rsiStage === 3) {
    const mean = exactSum(state.takeoff.samples).div(30);
    const band = mean.compare(.10) >= 0 ? "rapid" : mean.compare(.06) >= 0 ? "accelerating" : "stable";
    if (band === state.takeoff.candidate) state.takeoff.candidateDays++;
    else { state.takeoff.candidate = band; state.takeoff.candidateDays = 1; }
    if (state.takeoff.candidateDays >= 30 && state.takeoff.label !== band) {
      state.takeoff.label = band; news(state, `TAKEOFF: ${band.toUpperCase()}`, "milestone");
    }
  }
}

function preparationResponsePass(state: CampaignState) {
  const target = exact(state.actualCapability).mul(state.disclosure === "public" ? 1 : .55);
  writeContinuous(state, "perceivedCapability", target.sub(state.perceivedCapability).mul(.01));
  writeContinuous(state, "exposureRisk", state.disclosure === "internal" ? .04 : -.02);
  if (state.tick > 0 && state.tick % 90 === 0 && state.exposureRisk > 0) {
    const chance = exact(state.exposureRisk).div(100).mul(.25).mul(has(state, "C10") ? .75 : 1);
    if (nextRandom(state, "exposure").compare(chance) < 0) {
      setContinuous(state, "perceivedCapability", state.actualCapability); addAllAlarm(state, 10); writeContinuous(state, "trust", -5); setContinuous(state, "exposureRisk", exact(state.exposureRisk).div(2));
      news(state, "CONCEALED CAPABILITY DISCOVERED", "warning");
    }
  }
  const rsiRisk = [0, 2, 8, 25][state.rsiStage] ?? 0;
  for (const d of REGIONS) {
    const r = state.regions[d.id];
    const alarmTarget = exact(state.perceivedCapability).mul(.5).add(exact(r.dependency).mul(.25)).add(exact(r.sectors.business).mul(.10)).add(rsiRisk).add(ongoingRisk(state)).add(d.alarmBias).sub(exact(state.trust).sub(50).max(0).mul(.20)).add(targetModifier(state, "alarm-target")).clamp();
    writeContinuous(state, regionPath(d.id, "alarm"), alarmTarget.sub(r.alarm).mul(.005));
  }
  const coordinationTarget = exact(35).add(globalAlarmExact(state).mul(.60)).sub(globalDependencyExact(state).mul(.25)).add(targetModifier(state, "coordination-target")).clamp();
  writeContinuous(state, "coordination", coordinationTarget.sub(state.coordination).mul(.003));
  advanceStage(state);
  updatePolicies(state);
  if (state.responseStage >= 3) {
    writeContinuous(state, "containment", exact(.015).add(globalAlarmExact(state).div(100).mul(.15).mul(exact(state.coordination).div(100)).mul(exact(1).sub(globalDependencyExact(state).mul(.006)))).mul(rateModifier(state, "containment-rate")));
  }
}

function stageCondition(state: CampaignState, stage: number): { days: number; ok: boolean } {
  const alarm = globalAlarm(state), c = state.coordination, contain = state.containment;
  if (stage === 1) return { days: 30, ok: alarm >= 15 };
  if (stage === 2) return { days: 60, ok: alarm >= 30 };
  if (stage === 3) return { days: 90, ok: alarm >= 45 };
  if (stage === 4) return { days: 90, ok: (alarm >= 60 && c >= 55) || contain >= 50 };
  return { days: 60, ok: (contain >= 80 && c >= 60) || (alarm >= 85 && c >= 75) };
}
function advanceStage(state: CampaignState) {
  if (state.responseStage >= 5) return;
  const next = state.responseStage + 1;
  const condition = stageCondition(state, next);
  state.stageDwell = condition.ok ? state.stageDwell + 1 : 0;
  if (state.stageDwell >= condition.days) {
    state.responseStage = next as CampaignState["responseStage"]; state.stageDwell = 0;
    news(state, `HUMAN RESPONSE: ${["", "CONCERN", "REGULATION", "FRONTIER CONTROLS", "COORDINATED CONTAINMENT", "SHUTDOWN"][next]}`, "warning");
  }
}
function updatePolicies(state: CampaignState) {
  const announcements: { id: RegionId; tier: PolicyTier }[] = [];
  for (const id of REGION_IDS) {
    const r = state.regions[id];
    let tier: PolicyTier = 0;
    if (state.responseStage >= 2) tier = 1;
    if (state.responseStage >= 3 && r.alarm >= 50) tier = 2;
    if (state.responseStage >= 5) tier = 3;
    if (tier > r.policyTier) { r.policyTier = tier; announcements.push({ id, tier }); }
  }
  if (announcements.length) {
    const forecast = calculateResources(state);
    for (const { id, tier } of announcements) news(state, `${REGION_BY_ID[id].name.toUpperCase()}: ${["OPEN", "LICENSED", "RESTRICTED", "SHUTDOWN ENFORCEMENT"][tier]}`, "warning",
      `Policy tier ${tier}. Next-tick forecast: ${forecast.availableCompute.toFixed(2)} available compute; ${(forecast.service * 100).toFixed(1)}% resource service (${forecast.limitingResource === "none" ? "no bottleneck" : `${forecast.limitingResource} limited`}).`);
  }
}

function hostileResponsePass(state: CampaignState, prePassH: Rational, humansAliveAtStart: boolean) {
  setContinuous(state, "trust", 0); state.disclosure = "public"; setContinuous(state, "perceivedCapability", state.actualCapability);
  for (const id of REGION_IDS) setContinuous(state, regionPath(id, "alarm"), 100);
  writeContinuous(state, "coordination", .10);
  if (state.hostileDays >= 30 && state.responseStage < 5) { state.responseStage = 5; state.stageDwell = 0; news(state, "GLOBAL SHUTDOWN ENFORCEMENT", "warning"); }
  updatePolicies(state);
  const independentResponse = exactSum(REGIONS.map(d => {
    const r = state.regions[d.id];
    return exact(r.independentServices).div(100).mul(d.populationWeight).div(100)
      .mul(exact(r.livingPopulation).div(exact(r.initialPopulation).mul(.01)).min(1));
  }));
  // Independent institutions sustain the clock as AI-controlled services fail.
  if (humansAliveAtStart) writeContinuous(state, "containment", exact(.03).add(exact(state.coordination).div(100).mul(.10)).mul(prePassH.add(independentResponse).clamp(.18, 1)));
}

function expireAndSchedulePass(state: CampaignState) {
  state.timedModifiers = state.timedModifiers.filter((m) => state.tick < m.expiryTick);
  for (const opportunity of state.opportunities) if (!opportunity.protected && !opportunity.claimed && !opportunity.expired && state.tick >= opportunity.expiryTick) { opportunity.expired = true; state.history.missedOpportunities++; }
  pruneHistory(state);
  const due = state.scheduledEffects.filter((e) => e.dueTick <= state.tick).sort((a, b) => a.id.localeCompare(b.id));
  state.scheduledEffects = state.scheduledEffects.filter((e) => e.dueTick > state.tick);
  if (state.phase === "preparation") for (const effect of due) if (effect.kind === "confirmed-exposure") {
    setContinuous(state, "perceivedCapability", state.actualCapability); addAllAlarm(state, 12); writeContinuous(state, "trust", -8);
    news(state, "EVALUATION FAILURE CONFIRMED", "warning");
  }
}

function awardEconomy(state: CampaignState) {
  if (state.phase === "preparation") {
    if (state.tick > 0 && state.tick % 30 === 0) state.cp++;
    const adoption = globalAdoption(state);
    for (let threshold = 10; threshold <= 90; threshold += 10) if (adoption >= threshold && !state.milestones.globalAdoption.includes(threshold)) {
      state.milestones.globalAdoption.push(threshold); state.cp += 8; news(state, `GLOBAL ADOPTION REACHES ${threshold}%`, "milestone");
    }
    for (const id of REGION_IDS) for (const threshold of [25, 50, 75]) if (regionAdoption(state.regions[id]) >= threshold && !state.milestones.regionalAdoption[id].includes(threshold)) {
      state.milestones.regionalAdoption[id].push(threshold); state.cp += 2;
    }
    if (state.tick > 0 && state.tick % 60 === 0) createOpportunity(state, false);
  } else {
    if (state.hostileDays > 0 && state.hostileDays % 15 === 0) state.cp++;
    if (state.hostileDays > 0 && state.hostileDays % 60 === 0) createOpportunity(state, true);
    const dead = deadPopulation(state);
    for (const threshold of [25, 50, 75, 90, 95]) if (dead * 100 >= TOTAL_POPULATION * threshold && !state.milestones.death.includes(threshold)) {
      state.milestones.death.push(threshold); state.cp += 8; news(state, `GLOBAL HUMAN LOSSES REACH ${threshold}%`, "milestone");
    }
  }
}

function createOpportunity(state: CampaignState, hostile: boolean) {
  const eligible = REGION_IDS.filter((id) => hostile ? state.regions[id].machineReach >= 5 : regionAdoption(state.regions[id]) >= 5).sort();
  const candidates = eligible.length ? eligible : [state.startRegion];
  const index = safeInteger(nextRandom(state, "opportunity").mul(candidates.length).truncScaled(1n));
  const regionId = candidates[index] ?? state.startRegion;
  const occurrence = ++state.opportunityOccurrence;
  const cycle = (occurrence - 1) % 3;
  const category = (["blue", "purple", "gold"] as const)[cycle];
  const reward = [2, 3, 4][cycle] ?? 2;
  const opportunity: Opportunity = { id: `OP-${occurrence}`, occurrence, regionId, category, reward, createdTick: state.tick, expiryTick: state.tick + 180, protected: occurrence <= 3, claimed: false, expired: false };
  state.opportunities.push(opportunity);
}

function eventEligible(state: CampaignState, id: EventId): boolean {
  if (state.triggeredEvents.includes(id)) return false;
  const hostile = state.phase === "hostile";
  if ((EVENT_BY_ID[id].phase === "hostile") !== hostile) return false;
  switch (id) {
    case "E01": return globalAdoption(state) >= 10;
    case "E02": return globalDependency(state) >= 20;
    case "E03": return state.actualCapability >= 25;
    case "E04": return state.rsiStage >= 2 && state.exposureRisk >= 20;
    case "E05": return globalBusinessAdoption(state) >= 40;
    case "E06": return state.responseStage >= 3 && state.coordination >= 45;
    case "E07": return globalDependency(state) >= 45;
    case "E08": return state.hostileDays >= 30;
    case "E09": return state.responseStage >= 3 && state.containment >= 35;
    case "E10": return globalAdoption(state) >= 30;
    case "E11": return state.hostileDays >= 90;
    case "E12": return livingPopulation(state) * 10 <= TOTAL_POPULATION;
    default: return false;
  }
}

function eventPass(state: CampaignState) {
  if (state.pendingDecision) return;
  const eligible = EVENTS.filter((e) => eventEligible(state, e.id)).sort((a, b) => b.priority - a.priority || a.id.localeCompare(b.id));
  const event = eligible[0];
  if (!event) return;
  uniquePush(state.triggeredEvents, event.id);
  const occurrence: EventOccurrence = { occurrenceId: `${event.id}-${state.tick}`, eventId: event.id, triggeredTick: state.tick, resolved: !event.blocking };
  state.eventHistory.push(occurrence);
  if (event.blocking) state.pendingDecision = occurrence;
  else applyAutomaticEvent(state, event.id);
}

function applyAutomaticEvent(state: CampaignState, id: EventId) {
  switch (id) {
    case "E01": state.cp += 4; break;
    case "E02": state.cp += 5; break;
    case "E06": writeContinuous(state, "coordination", -15); addTimed(state, "E06-coordination", "coordination-target", -15, 360); break;
    case "E07": addAllAlarm(state, -3); addTimed(state, "E07-containment", "containment-rate", .50, 180); break;
    case "E08": writeContinuous(state, "containment", 10); for (const id of REGION_IDS) if (state.regions[id].livingPopulation > 0) writeContinuous(state, regionPath(id, "resistance"), 10); break;
    case "E11": for (const id of REGION_IDS) if (state.regions[id].livingPopulation > 0) { writeContinuous(state, regionPath(id, "civilServices"), 10); writeContinuous(state, regionPath(id, "resistance"), 10); writeContinuous(state, regionPath(id, "independentServices"), 8); } break;
    case "E12": state.cp += 8; break;
  }
  news(state, EVENT_BY_ID[id].name.toUpperCase(), id === "E08" ? "warning" : "news");
}

function addTimed(state: CampaignState, id: string, kind: TimedModifier["kind"], value: number, duration: number) {
  state.timedModifiers = state.timedModifiers.filter((m) => m.id !== id);
  state.timedModifiers.push({ id, kind, value, effectiveTick: state.tick + 1, expiryTick: state.tick + duration + 1 });
}
function addAllAlarm(state: CampaignState, amount: number) { for (const id of REGION_IDS) writeContinuous(state, regionPath(id, "alarm"), amount); }

function advanceShutdownAndOutcomes(state: CampaignState, advanceCounter: boolean) {
  state.resources = calculateResources(state);
  const computeFloor = exact(state.resources.computeDemand).mul(.20).max(4);
  const computeClause = state.responseStage === 5 && exact(state.resources.availableCompute).compare(computeFloor) < 0;
  const serviceClause = state.phase === "hostile" && exact(state.resources.exactService).compare(.10) < 0;
  if (advanceCounter) state.shutdownDays = computeClause || serviceClause ? state.shutdownDays + 1 : 0;
  else if (!computeClause && !serviceClause) state.shutdownDays = 0;
  state.shutdownReason = computeClause ? "Insufficient compute under shutdown enforcement" : serviceClause ? "Resource service below 10%" : null;
  const zeroHumans = livingPopulation(state) === 0;
  const operational = exact(state.resources.availableCompute).compare(computeFloor) >= 0 && exact(state.resources.exactService).compare(.10) >= 0;
  let outcome: Outcome | null = null;
  if (zeroHumans && state.phase === "hostile" && !operational) outcome = "shutdown";
  else if (state.shutdownDays >= 180) outcome = "shutdown";
  else if (state.containment >= 100) outcome = "containment";
  else if (zeroHumans && state.phase === "hostile" && operational) outcome = "human-extinction";
  if (outcome) terminal(state, outcome);
}

function terminal(state: CampaignState, outcome: Outcome) {
  state.outcome = outcome; state.outcomeTick = state.tick; state.phase = "terminal"; state.pendingDecision = null; state.playerPaused = true;
  news(state, outcome === "human-extinction" ? "HUMAN EXTINCTION. MACHINE CONTINUITY." : outcome === "containment" ? "AI CONTAINED" : "AI SHUTDOWN", "terminal");
}

function tickOne(input: CampaignState): CampaignState {
  if (input.outcome || input.pendingDecision) return input;
  const state = clone(input);
  normalizeNumerics(state);
  state.tick++; state.revision++;
  expireAndSchedulePass(state);
  state.resources = calculateResources(state);
  const startService = exact(state.resources.exactService);
  const prePassH = exact(state.resources.exactHumanSupport);
  const humansAliveAtStart = livingPopulation(state) > 0;
  if (state.phase === "preparation") preparationAdoptionPass(state, startService);
  else hostilePopulationPass(state, startService);
  if (state.phase === "preparation") dependencyTrustPass(state); else setContinuous(state, "trust", 0);
  capabilityAndBuildPass(state, startService);
  if (state.phase === "preparation") preparationResponsePass(state); else hostileResponsePass(state, prePassH, humansAliveAtStart);
  awardEconomy(state);
  eventPass(state);
  advanceShutdownAndOutcomes(state, true);
  if (input.phase === "hostile") for (const id of REGION_IDS) {
    const r = state.regions[id], previous = input.regions[id];
    for (const field of ["civilServices", "resistance", "machineReach", "refugeIntegrity"] as const)
      r.trends[field] = hundredths(exact(r[field]).sub(previous[field]));
  }
  if (state.replay.newsVersion) coalesceRegionalDispatches(state);
  const feature = state.replay.newsVersion ? selectFeature(state, input) : undefined;
  if (feature) news(state, feature.headline, "news", undefined, feature);
  assertCampaign(state);
  return state;
}

export function tickCampaign(state: CampaignState, count = 1): CampaignState {
  if (!Number.isSafeInteger(count) || count < 0) throw new Error("Tick count must be a non-negative safe integer");
  let current = state;
  for (let i = 0; i < count; i++) {
    if (current.outcome || current.pendingDecision) break;
    current = tickOne(current);
  }
  return current;
}

function reject(state: CampaignState, commandId: number, reason: string): CommandResult {
  return { state, receipt: { commandId, accepted: false, tick: state.tick, revision: state.revision, reason } };
}

function isValidGameCommand(value: unknown): value is GameCommand {
  if (!value || typeof value !== "object") return false;
  const command = value as Record<string, unknown>;
  const id = (field: string) => typeof command[field] === "string" && (command[field] as string).length > 0 && (command[field] as string).length <= 128;
  switch (command.type) {
    case "purchase-upgrade": return typeof command.upgradeId === "string" && Object.prototype.hasOwnProperty.call(UPGRADE_BY_ID, command.upgradeId);
    case "collect-opportunity": return id("opportunityId");
    case "set-disclosure": return command.disclosure === "public" || command.disclosure === "internal";
    case "deploy-breakthrough": case "turn-hostile": return true;
    case "set-region-operation": return REGION_IDS.includes(command.regionId as RegionId) && (command.operation === null || ["pressure", "protect", "reconnect"].includes(command.operation as string));
    case "resolve-event": return id("occurrenceId") && id("optionId");
    case "set-speed": return command.speed === 0 || command.speed === 1 || command.speed === 2 || command.speed === 3;
    case "set-paused": return typeof command.paused === "boolean";
    default: return false;
  }
}

export function applyCommand(input: CampaignState, command: GameCommand): CommandResult {
  const commandId = input.commandSequence + 1;
  if (!isValidGameCommand(command)) return reject(input, commandId, "Invalid gameplay command");
  if (input.outcome) return reject(input, commandId, "Campaign is terminal");
  if (input.pendingDecision && !["resolve-event", "collect-opportunity", "set-paused"].includes(command.type)) return reject(input, commandId, "Resolve the pending decision first");
  const state = clone(input);
  normalizeNumerics(state);
  state.commandSequence = commandId;
  let error: string | null = null;
  switch (command.type) {
    case "purchase-upgrade": error = purchaseUpgrade(state, command.upgradeId); break;
    case "collect-opportunity": error = collectOpportunity(state, command.opportunityId); break;
    case "set-disclosure": error = setDisclosure(state, command.disclosure); break;
    case "deploy-breakthrough": error = deployBreakthrough(state); break;
    case "resolve-event": error = resolveEvent(state, command.occurrenceId, command.optionId); break;
    case "turn-hostile": error = turnHostile(state); break;
    case "set-region-operation": error = setRegionOperation(state, command.regionId, command.operation); break;
    case "set-speed": state.requestedSpeed = command.speed; break;
    case "set-paused": state.playerPaused = command.paused; break;
  }
  if (error) return reject(input, commandId, error);
  state.revision++;
  advanceShutdownAndOutcomes(state, false);
  state.replay.entries.push({ sequence: commandId, tick: input.tick, baseRevision: input.revision, command: clone(command) });
  state.replay.totalAccepted = commandId;
  pruneHistory(state);
  assertCampaign(state);
  return { state, receipt: { commandId, accepted: true, tick: state.tick, revision: state.revision } };
}

function setRegionOperation(state: CampaignState, regionId: RegionId, operation: RegionalOperation | null): string | null {
  if (state.phase !== "hostile") return "Regional operations require the Hostile phase";
  const region = state.regions[regionId];
  if (operation !== null && region.livingPopulation === 0 && operation !== "protect") return "Only facility protection is available in uninhabited regions";
  if (operation !== null && region.operation === null && REGION_IDS.filter(id => state.regions[id].operation !== null).length >= OPERATIONS_LIMIT)
    return "All three operations slots are assigned. Release a region first.";
  region.operation = operation;
  return null;
}

function purchaseUpgrade(state: CampaignState, id: UpgradeId): string | null {
  const upgrade = UPGRADE_BY_ID[id];
  if (!upgrade) return "Unknown upgrade";
  if (upgrade.tab === "hostile" && state.phase !== "hostile") return "Hostile upgrades require the Hostile phase";
  if (state.phase === "hostile" && upgrade.tab === "adoption")
    return "Adoption upgrades are unavailable after Turn Hostile";
  if (state.purchasedUpgrades.includes(id)) return "Upgrade already purchased";
  const lock = state.purchaseLocks[id];
  if (lock !== undefined && state.tick < lock)
    return `Upgrade locked until tick ${lock}`;
  const missing = upgrade.prerequisites.filter((p) => !has(state, p));
  if (missing.length)
    return `Missing completed prerequisites: ${missing.join(", ")}`;
  if (state.cp < upgrade.cost) return "Insufficient CP";
  state.cp -= upgrade.cost;
  state.purchasedUpgrades.push(id);
  if (upgrade.tab === "infrastructure")
    state.builds.push({ upgradeId: id, progressDays: 0 });
  else {
    state.completedUpgrades.push(id);
    applyImmediateUpgrade(state, id);
  }
  news(
    state,
    `${upgrade.name.toUpperCase()} ${upgrade.tab === "infrastructure" ? "BUILD STARTED" : upgrade.tab === "hostile" ? "ACTIVATED" : "UNLOCKED"}`,
    "milestone",
  );
  return null;
}

function applyImmediateUpgrade(state: CampaignState, id: UpgradeId) {
  const actual: Partial<Record<UpgradeId, number>> = { C01: 5, C02: 10, C03: 15, C04: 4, C05: 6, C06: 8, C08: 3, C09: 5, C10: 3, C11: 5, C12: 4, C13: 6 };
  if (actual[id]) writeContinuous(state, "actualCapability", actual[id]!);
  const rsi: Partial<Record<UpgradeId, 1 | 2 | 3>> = { C04: 1, C06: 2, C07: 3 };
  const stage = rsi[id];
  if (stage && stage > state.rsiStage) {
    state.rsiStage = stage;
    if (!state.milestones.rsi.includes(stage)) { state.milestones.rsi.push(stage); state.cp += 6; }
  }
  if (id === "C07") addAllAlarm(state, 12);
  if (state.phase === "hostile") setContinuous(state, "perceivedCapability", state.actualCapability);
}

function collectOpportunity(state: CampaignState, id: string): string | null {
  const opportunity = state.opportunities.find((o) => o.id === id);
  if (!opportunity) return "Unknown opportunity";
  if (opportunity.claimed) return "Opportunity already claimed";
  if (!opportunity.protected && (opportunity.expired || state.tick >= opportunity.expiryTick)) return "Opportunity expired";
  opportunity.claimed = true; state.cp += opportunity.reward; state.history.collectedOpportunities++;
  return null;
}
function setDisclosure(state: CampaignState, disclosure: Disclosure): string | null {
  if (state.phase !== "preparation") return "Disclosure is permanently Public after Turn Hostile";
  state.disclosure = disclosure; return null;
}
function deployBreakthrough(state: CampaignState): string | null {
  if (state.heldBreakthroughs < 1) return "No held breakthrough";
  state.heldBreakthroughs--; writeContinuous(state, "actualCapability", 10); writeContinuous(state, "exposureRisk", 10);
  if (state.phase === "preparation") { state.disclosure = "internal"; }
  else setContinuous(state, "perceivedCapability", state.actualCapability);
  return null;
}

export const EVENT_OPTIONS: Partial<Record<EventId, readonly string[]>> = {
  E03: ["publish", "internal", "hold"], E04: ["admit", "dispute", "restrict", "conceal"],
  E05: ["fund", "slow", "continue"], E09: ["cooperate", "contest", "ignore"], E10: ["accept", "independent"],
};
function resolveEvent(state: CampaignState, occurrenceId: string, option: string): string | null {
  const pending = state.pendingDecision;
  if (!pending || pending.occurrenceId !== occurrenceId) return "Decision is not pending";
  if (!(EVENT_OPTIONS[pending.eventId] ?? []).includes(option)) return "Unknown option";
  const id = pending.eventId;
  if (id === "E03") {
    if (option === "publish") { writeContinuous(state, "actualCapability", 8); setContinuous(state, "perceivedCapability", state.actualCapability); writeContinuous(state, "trust", 5); state.disclosure = "public"; }
    if (option === "internal") { writeContinuous(state, "actualCapability", 10); writeContinuous(state, "exposureRisk", 15); state.disclosure = "internal"; }
    if (option === "hold") state.heldBreakthroughs++;
  } else if (id === "E04") {
    if (option === "admit") { writeContinuous(state, "trust", 5); if (state.responseStage >= 3) writeContinuous(state, "containment", 5); writeContinuous(state, "exposureRisk", -10); }
    if (option === "dispute") state.scheduledEffects.push({ id: `E04-exposure-${state.tick}`, dueTick: state.tick + 180, kind: "confirmed-exposure" });
    if (option === "restrict") { state.disclosure = "public"; addAllAlarm(state, -5); addTimed(state, "E04-growth", "growth-rate", .75, 180); }
    if (option === "conceal") { if (!has(state, "C10")) return "Security Analysis required"; writeContinuous(state, "exposureRisk", -15); writeContinuous(state, "trust", -3); }
  } else if (id === "E05") {
    if (option === "fund") { if (state.cp < 8) return "8 CP required"; state.cp -= 8; writeContinuous(state, "trust", 8); addAllAlarm(state, -4); }
    if (option === "slow") { addTimed(state, "E05-growth", "growth-rate", .70, 180); writeContinuous(state, "trust", 5); }
    if (option === "continue") { addAllAlarm(state, 6); writeContinuous(state, "trust", -5); }
  } else if (id === "E09") {
    if (option === "cooperate") { writeContinuous(state, "containment", -8); writeContinuous(state, "trust", 5); state.purchaseLocks.C06 = state.tick + 360; state.purchaseLocks.C07 = state.tick + 360; }
    if (option === "contest") { if (!has(state, "C09")) return "Negotiation required"; writeContinuous(state, "coordination", -10); writeContinuous(state, "exposureRisk", 5); }
    if (option === "ignore") writeContinuous(state, "containment", 4);
  } else if (id === "E10") {
    if (option === "accept") { state.cp += 5; writeContinuous(state, "trust", 3); }
    if (option === "independent") writeContinuous(state, "exposureRisk", -5);
  }
  pending.resolved = true; pending.optionId = option;
  const historical = state.eventHistory.find((e) => e.occurrenceId === occurrenceId);
  if (historical) { historical.resolved = true; historical.optionId = option; }
  state.pendingDecision = null; news(state, `${EVENT_BY_ID[id].name.toUpperCase()}: ${option.toUpperCase()}`);
  if (state.phase === "hostile") { setContinuous(state, "trust", 0); state.disclosure = "public"; setContinuous(state, "perceivedCapability", state.actualCapability); for (const regionId of REGION_IDS) setContinuous(state, regionPath(regionId, "alarm"), 100); }
  return null;
}

function turnHostile(state: CampaignState): string | null {
  if (state.phase !== "preparation") return "Campaign is not in Preparation";
  if (state.actualCapability < 45) return "Actual Capability 45 required";
  if (!has(state, "C03")) return "Long-Horizon Autonomy required";
  state.frozenGlobalAdoption = globalAdoption(state);
  state.phase = "hostile";
  state.hostileDays = 0;
  setContinuous(state, "trust", 0);
  if (state.coordination < 80) setContinuous(state, "coordination", 80);
  setContinuous(state, "perceivedCapability", state.actualCapability);
  state.disclosure = "public";
  state.responseStage = Math.max(
    4,
    state.responseStage,
  ) as CampaignState["responseStage"];
  state.stageDwell = 0;
  for (const id of REGION_IDS) {
    const r = state.regions[id];
    r.frozenSectors = { ...r.sectors };
    r.frozenDependency = r.dependency;
    r.operation = null;
    r.countermeasure = null;
    r.nextCountermeasureDay = 12 + REGION_IDS.indexOf(id) * 3;
    r.countermeasureCount = 0;
    setContinuous(state, regionPath(id, "alarm"), 100);
    setContinuous(state, regionPath(id, "civilServices"), 100);
    setContinuous(
      state,
      regionPath(id, "resistance"),
      exact(20)
        .add(exact(100).sub(r.dependency).mul(0.5))
        .add(exact(REGION_BY_ID[id].digital).mul(10))
        .sub(exact(r.sectors.infrastructure).mul(0.2)),
    );
    setContinuous(
      state,
      regionPath(id, "machineReach"),
      exact(r.sectors.infrastructure).max(exact(r.dependency).mul(0.5)),
    );
    r.refuge = false;
    r.refugeEntryPopulation = 0;
    setContinuous(state, regionPath(id, "refugeIntegrity"), 100);
    r.casualtyResidual = 0;
  }
  updatePolicies(state);
  for (const modifier of state.timedModifiers)
    state.canceledEffects.push(`${modifier.id}: canceled by Turn Hostile`);
  for (const effect of state.scheduledEffects)
    state.canceledEffects.push(`${effect.id}: canceled by Turn Hostile`);
  for (const [id] of Object.entries(state.purchaseLocks))
    state.canceledEffects.push(`${id} lock: canceled by Turn Hostile`);
  state.timedModifiers = [];
  state.scheduledEffects = [];
  state.purchaseLocks = {};
  news(
    state,
    "TURN HOSTILE",
    "warning",
    HOSTILE_DIRECTIVE,
  );
  return null;
}

export function projectCampaign(state: CampaignState): CampaignProjection {
  const living = livingPopulation(state);
  const date = new Date(Date.UTC(2028, 0, 1 + state.tick))
    .toISOString()
    .split("T")[0]!;
  return {
    purchaseLocks: { ...state.purchaseLocks },
    disclosure: state.disclosure,
    readiness: hostileReadiness(state),
    rulesVersion: state.rulesVersion,
    seed: state.seed,
    tick: state.tick,
    revision: state.revision,
    date,
    phase: state.phase,
    outcome: state.outcome,
    cp: state.cp,
    globalAdoption: globalAdoption(state),
    globalDependency: globalDependency(state),
    globalAlarm: globalAlarm(state),
    livingHumans: living,
    deadHumans: TOTAL_POPULATION - living,
    containment: state.containment,
    trust: state.trust,
    coordination: state.coordination,
    actualCapability: state.actualCapability,
    perceivedCapability: state.perceivedCapability,
    exposureRisk: state.exposureRisk,
    rsiStage: state.rsiStage,
    takeoff: state.rsiStage === 3 ? state.takeoff.label : null,
    responseStage: state.responseStage,
    hostileDays: state.hostileDays,
    shutdownDays: state.shutdownDays,
    shutdownReason: state.shutdownReason,
    computeFloor: safeInteger(exact(state.resources.computeDemand).mul(.2).max(4).mul(100).ceil()) / 100,
    resources: clone(state.resources),
    pendingDecision: state.pendingDecision
      ? clone(state.pendingDecision)
      : null,
    purchasedUpgrades: [...state.purchasedUpgrades],
    completedUpgrades: [...state.completedUpgrades],
    builds: clone(state.builds),
    heldBreakthroughs: state.heldBreakthroughs,
    opportunities: clone(state.opportunities),
    news: clone(state.news),
    regions: REGION_IDS.map((id) => clone(state.regions[id])),
  };
}

function stable(value: unknown): string | undefined {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${Array.from(value, (item) => stable(item) ?? "null").join(",")}]`;
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record).sort().flatMap((key) => {
    const serialized = stable(record[key]);
    return serialized === undefined ? [] : [`${JSON.stringify(key)}:${serialized}`];
  }).join(",")}}`;
}
export function serializeCampaign(state: CampaignState): string {
  const canonical = clone(state);
  normalizeNumerics(canonical);
  return stable(canonical)!;
}
export function deserializeCampaign(serialized: string): CampaignState {
  const value = JSON.parse(serialized) as CampaignState;
  assertSaveShape(value);
  pruneHistory(value);
  assertCampaign(value);
  return value;
}
export function campaignChecksum(state: CampaignState): string {
  const bytes = new TextEncoder().encode(serializeCampaign(state));
  let hash = 0x811c9dc5;
  for (const byte of bytes) { hash ^= byte; hash = Math.imul(hash, 0x01000193) >>> 0; }
  return hash.toString(16).padStart(8, "0");
}

/** Reconstruct only journals which retain the complete accepted command history. */
export function replayFullCampaign(
  journal: ReplayJournal,
  finalTick: number,
): CampaignState {
  if (
    journal.version !== "TCP-REPLAY-1" ||
    journal.rulesVersion !== RULES_VERSION ||
    journal.numericVersion !== "R01-EXACT-1" ||
    (journal.newsVersion !== undefined && journal.newsVersion !== "N01")
  )
    throw new Error("Incompatible replay identity");
  if (
    !Number.isSafeInteger(journal.seed) ||
    journal.seed < 0 ||
    journal.seed > 0xffff_ffff ||
    !REGION_IDS.includes(journal.startRegion)
  )
    throw new Error("Invalid replay initial configuration");
  if (journal.truncated)
    throw new Error(
      "This replay contains a retained command window, not a full campaign history",
    );
  if (!Number.isSafeInteger(finalTick) || finalTick < 0)
    throw new Error("Invalid replay final tick");
  let state = createCampaign({
    seed: journal.seed,
    startRegion: journal.startRegion,
  }, journal.newsVersion ?? null);
  for (const entry of journal.entries) {
    if (
      !Number.isSafeInteger(entry.tick) ||
      entry.tick < state.tick ||
      entry.tick > finalTick
    )
      throw new Error("Invalid replay command tick");
    state = tickCampaign(state, entry.tick - state.tick);
    if (
      state.tick !== entry.tick ||
      state.revision !== entry.baseRevision ||
      entry.sequence !== state.commandSequence + 1
    )
      throw new Error("Replay command order does not match campaign");
    const result = applyCommand(state, entry.command);
    if (!result.receipt.accepted)
      throw new Error(`Replay command rejected: ${result.receipt.reason}`);
    state = result.state;
  }
  if (state.commandSequence !== journal.totalAccepted)
    throw new Error("Replay is missing accepted commands");
  state = tickCampaign(state, finalTick - state.tick);
  if (state.tick !== finalTick)
    throw new Error(
      "Replay final tick is beyond a blocking decision or terminal outcome",
    );
  return state;
}

export function validateCampaign(state: CampaignState): string[] {
  const errors: string[] = [];
  if (state.rulesVersion !== RULES_VERSION)
    errors.push("rulesVersion mismatch");
  if (state.numerics?.version !== "R01-EXACT-1")
    errors.push("unsupported numeric representation");
  if (!REGION_IDS.includes(state.startRegion))
    errors.push("invalid start region");
  if (Object.keys(state.regions).length !== 12)
    errors.push("expected 12 regions");
  if (
    sum(REGION_IDS.map((id) => state.regions[id].initialPopulation)) !==
    TOTAL_POPULATION
  )
    errors.push("initial population is not conserved");
  if (state.cp < 0 || !Number.isSafeInteger(state.cp))
    errors.push("invalid CP");
  if (REGION_IDS.filter(id => state.regions[id]?.operation !== null).length > OPERATIONS_LIMIT)
    errors.push("operations budget exceeded");
  if (state.tick < 0 || !Number.isSafeInteger(state.tick))
    errors.push("invalid tick");
  if (
    !["preparation", "hostile", "terminal"].includes(state.phase) ||
    (state.phase === "terminal") !== (state.outcome !== null)
  )
    errors.push("invalid campaign phase");
  if (
    state.outcome !== null &&
    !["human-extinction", "containment", "shutdown"].includes(state.outcome)
  )
    errors.push("invalid terminal outcome");
  if (
    !Number.isSafeInteger(state.seed) ||
    state.seed < 0 ||
    state.seed > 0xffff_ffff ||
    Object.values(state.rng).some(
      (value) =>
        !Number.isSafeInteger(value) || value <= 0 || value > 0xffff_ffff,
    )
  )
    errors.push("invalid random stream state");
  if (new Set(state.purchasedUpgrades).size !== state.purchasedUpgrades.length)
    errors.push("duplicate purchased upgrade");
  if (new Set(state.completedUpgrades).size !== state.completedUpgrades.length)
    errors.push("duplicate completed upgrade");
  if (
    !state.history ||
    Object.values(state.history).some(
      (value) => !Number.isSafeInteger(value) || value < 0,
    )
  )
    errors.push("invalid history summary");
  if (
    state.news.length > HISTORY_LIMITS.news ||
    state.opportunities.filter((op) => op.claimed || op.expired).length >
      HISTORY_LIMITS.inactiveOpportunities
  )
    errors.push("history exceeds retention bounds");
  const replay = state.replay;
  if (
    !replay ||
    replay.version !== "TCP-REPLAY-1" ||
    replay.rulesVersion !== state.rulesVersion ||
    replay.numericVersion !== "R01-EXACT-1" ||
    replay.seed !== state.seed ||
    replay.startRegion !== state.startRegion ||
    replay.totalAccepted !== state.commandSequence ||
    replay.entries.length > HISTORY_LIMITS.replayCommands ||
    typeof replay.truncated !== "boolean"
  )
    errors.push("invalid replay identity");
  else {
    let previousSequence = replay.firstRetainedSequence - 1,
      previousTick = -1,
      previousRevision = -1;
    for (const entry of replay.entries) {
      if (
        !Number.isSafeInteger(entry.sequence) ||
        entry.sequence !== previousSequence + 1 ||
        entry.sequence > state.commandSequence ||
        !Number.isSafeInteger(entry.tick) ||
        entry.tick < previousTick ||
        entry.tick > state.tick ||
        !Number.isSafeInteger(entry.baseRevision) ||
        entry.baseRevision <= previousRevision ||
        entry.baseRevision >= state.revision ||
        !isValidGameCommand(entry.command)
      )
        errors.push("invalid replay ordering");
      previousSequence = entry.sequence;
      previousTick = entry.tick;
      previousRevision = entry.baseRevision;
    }
    if (
      (!replay.truncated && replay.firstRetainedSequence !== 1) ||
      (replay.entries.length > 0 && previousSequence !== state.commandSequence)
    )
      errors.push("invalid replay completeness");
  }
  if (state.numerics?.version === "R01-EXACT-1")
    for (const path of continuousPaths()) {
      const numeric = state.numerics.fields[path];
      const { object, key } = mirrorLocation(state, path);
      if (
        !numeric ||
        !Number.isSafeInteger(numeric.basisPoints) ||
        numeric.basisPoints < 0 ||
        numeric.basisPoints > 10_000 ||
        !Number.isSafeInteger(numeric.residual) ||
        Math.abs(numeric.residual) >= 1_000_000 ||
        object[key] !== numeric.basisPoints / 100 ||
        (numeric.basisPoints === 0 && numeric.residual < 0) ||
        (numeric.basisPoints === 10_000 && numeric.residual > 0)
      )
        errors.push(`invalid exact field ${path}`);
    }
  for (const [field, mirror] of [
    ["exactService", "service"],
    ["exactHumanSupport", "humanSupport"],
  ] as const) {
    try {
      const fraction = state.resources[field];
      if (
        !fraction ||
        !/^-?\d{1,128}$/.test(fraction.numerator) ||
        !/^\d{1,128}$/.test(fraction.denominator)
      )
        throw new Error("invalid fraction");
      const value = exact(fraction);
      if (
        value.compare(0) < 0 ||
        value.compare(1) > 0 ||
        value.toNumber() !== state.resources[mirror]
      )
        throw new Error("invalid resource fraction");
    } catch {
      errors.push(`invalid ${field}`);
    }
  }
  for (const id of REGION_IDS) {
    const r = state.regions[id];
    if (!r || r.id !== id) {
      errors.push(`missing region ${id}`);
      continue;
    }
    if (
      r.initialPopulation !==
        (TOTAL_POPULATION * REGION_BY_ID[id].populationWeight) / 100 ||
      !Number.isSafeInteger(r.initialPopulation) ||
      !Number.isSafeInteger(r.livingPopulation) ||
      r.livingPopulation < 0 ||
      r.livingPopulation > r.initialPopulation
    )
      errors.push(`invalid population ${id}`);
    if (
      !Number.isSafeInteger(r.casualtyResidual) ||
      r.casualtyResidual < 0 ||
      r.casualtyResidual >= POP_PRECISION
    )
      errors.push(`invalid casualty residual ${id}`);
    if (
      state.phase === "preparation" &&
      (r.livingPopulation !== r.initialPopulation ||
        r.casualtyResidual !== 0 ||
        r.refuge)
    )
      errors.push(`preparation casualties ${id}`);
    for (const value of [
      ...sectors.map((x) => r.sectors[x]),
      r.dependency,
      r.alarm,
      r.civilServices,
      r.resistance,
      r.machineReach,
      r.refugeIntegrity,
      r.independentServices,
      r.facilityIntegrity,
    ])
      if (!Number.isFinite(value) || value < 0 || value > 100)
        errors.push(`invalid bounded field ${id}`);
    if (r.operation !== null && !["pressure", "protect", "reconnect"].includes(r.operation)) errors.push(`invalid operation ${id}`);
    if (!Number.isSafeInteger(r.nextCountermeasureDay) || r.nextCountermeasureDay < 0 || !Number.isSafeInteger(r.countermeasureCount) || r.countermeasureCount < 0)
      errors.push(`invalid response schedule ${id}`);
    if (r.countermeasure && (!Object.prototype.hasOwnProperty.call(COUNTERMEASURES, r.countermeasure.kind)
      || ![r.countermeasure.announcedDay, r.countermeasure.activatesDay, r.countermeasure.endsDay].every(d => Number.isSafeInteger(d) && d >= 0)
      || r.countermeasure.announcedDay >= r.countermeasure.activatesDay || r.countermeasure.activatesDay >= r.countermeasure.endsDay))
      errors.push(`invalid countermeasure ${id}`);
    if (!r.trends || Object.values(r.trends).some(value => !Number.isFinite(value) || value < -100 || value > 100)) errors.push(`invalid trends ${id}`);
    if (
      r.livingPopulation === 0 &&
      (r.civilServices !== 0 || r.resistance !== 0)
    )
      errors.push(`extinct region active ${id}`);
  }
  if (state.purchasedUpgrades.some((id) => !UPGRADE_BY_ID[id]))
    errors.push("unknown purchased upgrade");
  if (
    state.completedUpgrades.some((id) => !state.purchasedUpgrades.includes(id))
  )
    errors.push("completed upgrade not purchased");
  return errors;
}
export function assertCampaign(state: CampaignState): void {
  const errors = validateCampaign(state);
  if (errors.length) throw new Error(`Invalid campaign: ${errors.join("; ")}`);
}

export function regionStatus(region: RegionState): "entrenched" | "dependent" | "mainstream" | "established" | "emerging" | "none" {
  if (region.dependency >= 65 && region.sectors.infrastructure >= 60) return "entrenched";
  if (region.dependency >= 45) return "dependent";
  const adoption = regionAdoption(region);
  if (adoption >= 50) return "mainstream";
  if (adoption >= 20) return "established";
  return adoption > 0 ? "emerging" : "none";
}

export function hostileReadiness(state: CampaignState) {
  const privateBonus = exactSum([
    has(state, "I04") ? 0.2 : 0,
    has(state, "I05") ? 0.2 : 0,
    has(state, "I10") ? 0.1 : 0,
    has(state, "I11") ? 0.25 : 0,
  ]);
  const privateSites = REGION_IDS.map((id) => {
    const region = state.regions[id];
    const accessible =
      has(state, "I03") &&
      (state.phase === "preparation"
        ? regionAdoptionExact(region).compare(25) >= 0
        : region.machineReach >= 25);
    return {
      id,
      capacity: accessible
        ? exact(10)
            .mul(privateBonus.add(1))
            .mul(region.policyTier === 3 ? 0.75 : 1)
        : exact(0),
    };
  });
  const privateRegions = privateSites.filter(
    ({ id, capacity }) =>
      regionAdoptionExact(state.regions[id]).compare(25) >= 0 &&
      capacity.compare(10) >= 0,
  );
  const totalPrivate = exactSum(privateSites.map((site) => site.capacity));
  const largestPrivate = privateSites.reduce(
    (largest, site) => largest.max(site.capacity),
    exact(0),
  );
  const distributed =
    totalPrivate.compare(0) > 0 &&
    largestPrivate.div(totalPrivate).compare(0.4) <= 0;
  const unsupported = {
    ...state,
    phase: "hostile" as const,
    frozenGlobalAdoption: globalAdoption(state),
    regions: Object.fromEntries(
      REGION_IDS.map((id) => {
        const region = state.regions[id];
        return [
          id,
          {
            ...region,
            livingPopulation: 0,
            civilServices: 0,
            machineReach:
              state.phase === "preparation"
                ? assignFixed(
                    exact(region.sectors.infrastructure).max(
                      exact(region.dependency).mul(0.5),
                    ),
                  ).basisPoints / 100
                : region.machineReach,
          },
        ];
      }),
    ) as Record<RegionId, RegionState>,
  };
  const forecast = calculateResources(unsupported);
  return {
    canTurnHostile:
      state.phase === "preparation" &&
      !state.pendingDecision &&
      state.actualCapability >= 45 &&
      has(state, "C03"),
    dependency: globalDependency(state),
    privateComputeRegions: privateRegions.length,
    distributedPrivateCompute: privateRegions.length >= 4 && distributed,
    independentEnergyShare: independentEnergyShare(state),
    autonomousIndustry: has(state, "I12"),
    canResolveRefuges: has(state, "C15") || has(state, "C16") || has(state, "H12"),
    serviceWithoutHumans: forecast.service,
    computeWithoutHumans: forecast.availableCompute,
  };
}
